/**
 * CompetitorDataPanel - Card grid of competitor market data from FMP API.
 *
 * Displays each competitor's price, change, market cap, P/E, EPS,
 * 52-week range (with progress bar), and basic company info.
 */

import { Database, ExternalLink, TrendingUp, TrendingDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { CompetitorData } from '@/hooks/useStrategicStream';

interface CompetitorDataPanelProps {
  competitors: CompetitorData[];
  className?: string;
}

export function CompetitorDataPanel({ competitors, className }: CompetitorDataPanelProps) {
  if (competitors.length === 0) {
    return (
      <div className="text-center py-8 text-text-muted text-sm">
        No competitor market data was fetched for this analysis.
      </div>
    );
  }

  // Find the latest fetchedAt timestamp
  const latestFetch = competitors
    .map(c => c.fetchedAt)
    .filter(Boolean)
    .sort()
    .pop();

  return (
    <div className={cn('space-y-4', className)}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Database className="w-4 h-4 text-text-muted" />
          <span className="text-xs font-medium text-text-secondary uppercase tracking-wide">
            Competitor Landscape
          </span>
          <span className="px-1.5 py-0.5 rounded bg-surface-light text-text-muted text-[10px]">
            {competitors.length}
          </span>
        </div>
        {latestFetch && (
          <span className="text-[10px] text-text-muted">
            Data as of {new Date(latestFetch).toLocaleDateString()}
          </span>
        )}
      </div>

      {/* Card grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {competitors.map((comp) => (
          <CompetitorCard key={comp.ticker} data={comp} />
        ))}
      </div>
    </div>
  );
}

function CompetitorCard({ data }: { data: CompetitorData }) {
  const isPositive = (data.change ?? 0) >= 0;
  const changeColor = isPositive ? 'text-green-400' : 'text-red-400';
  const ChangeIcon = isPositive ? TrendingUp : TrendingDown;

  // 52-week range progress
  let rangePercent: number | null = null;
  if (data.yearHigh != null && data.yearLow != null && data.price != null) {
    const range = data.yearHigh - data.yearLow;
    if (range > 0) {
      rangePercent = ((data.price - data.yearLow) / range) * 100;
    }
  }

  return (
    <div className="rounded-lg bg-surface-light/50 border border-border/50 p-3 space-y-2">
      {/* Header: ticker + company */}
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-mono font-bold text-sm text-text-primary">{data.ticker}</span>
            {data.sector && (
              <span className="text-[10px] text-text-muted truncate">{data.sector}</span>
            )}
          </div>
          <div className="text-xs text-text-muted truncate">{data.companyName}</div>
        </div>
        {data.website && (
          <a
            href={data.website}
            target="_blank"
            rel="noopener noreferrer"
            className="text-text-muted hover:text-text-secondary transition-colors flex-shrink-0"
          >
            <ExternalLink className="w-3 h-3" />
          </a>
        )}
      </div>

      {/* Price + change */}
      {data.price != null && (
        <div className="flex items-baseline gap-2">
          <span className="font-mono text-lg font-bold text-text-primary">
            {data.priceFormatted || `$${data.price.toFixed(2)}`}
          </span>
          {data.changePercent != null && (
            <span className={cn('flex items-center gap-0.5 text-xs font-medium', changeColor)}>
              <ChangeIcon className="w-3 h-3" />
              {data.changeFormatted || `${isPositive ? '+' : ''}${data.changePercent.toFixed(2)}%`}
            </span>
          )}
        </div>
      )}

      {/* Key metrics */}
      <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
        {data.marketCap != null && (
          <div>
            <span className="text-text-muted">Mkt Cap</span>
            <span className="ml-1 text-text-secondary">
              {data.marketCapFormatted || formatLargeNumber(data.marketCap)}
            </span>
          </div>
        )}
        {data.peRatio != null && (
          <div>
            <span className="text-text-muted">P/E</span>
            <span className="ml-1 text-text-secondary">
              {data.peFormatted || `${data.peRatio.toFixed(1)}x`}
            </span>
          </div>
        )}
        {data.eps != null && (
          <div>
            <span className="text-text-muted">EPS</span>
            <span className="ml-1 text-text-secondary">
              {data.epsFormatted || `$${data.eps.toFixed(2)}`}
            </span>
          </div>
        )}
        {data.employees != null && (
          <div>
            <span className="text-text-muted">Employees</span>
            <span className="ml-1 text-text-secondary">
              {data.employees.toLocaleString()}
            </span>
          </div>
        )}
      </div>

      {/* 52-week range */}
      {data.yearLow != null && data.yearHigh != null && (
        <div>
          <div className="flex items-center justify-between text-[10px] text-text-muted mb-0.5">
            <span>${data.yearLow.toFixed(0)}</span>
            <span className="text-text-muted">52W Range</span>
            <span>${data.yearHigh.toFixed(0)}</span>
          </div>
          <div className="h-1.5 rounded-full bg-surface-raised/50 overflow-hidden">
            {rangePercent != null && (
              <div
                className={cn(
                  'h-full rounded-full transition-all duration-500',
                  rangePercent > 70 ? 'bg-green-400' : rangePercent > 30 ? 'bg-amber-400' : 'bg-red-400'
                )}
                style={{ width: `${Math.max(2, Math.min(100, rangePercent))}%` }}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function formatLargeNumber(num: number): string {
  if (num >= 1e12) return `$${(num / 1e12).toFixed(1)}T`;
  if (num >= 1e9) return `$${(num / 1e9).toFixed(1)}B`;
  if (num >= 1e6) return `$${(num / 1e6).toFixed(0)}M`;
  return `$${num.toLocaleString()}`;
}
